import type {
  GlobalSearchResourceItem,
  GlobalSearchUserResult,
  ISchedule,
  Office
} from "@gettactic/api";
import { useCallback } from "react";
import type { Setting } from "../constants";
import { useGlobalSearchData } from "../GlobalSearchDataProvider";

interface UseSearchItemSelectionParams {
  filteredActions: Setting[];
  resources: GlobalSearchResourceItem[];
  people: GlobalSearchUserResult[];
  filteredOffices: Office[];
  filteredSettings: Setting[];
  navigation: {
    handleSettingAction: (settingId: string) => void;
    navigateToResource: (resourceId: string) => void;
    navigateToPerson: (person: GlobalSearchUserResult, schedules: ISchedule[]) => void;
    navigateToOffice: (officeId: string) => void;
    navigateToSetting: (url: string) => void;
  };
}

export function useSearchItemSelection({
  filteredActions,
  resources,
  people,
  filteredOffices,
  filteredSettings,
  navigation
}: UseSearchItemSelectionParams) {
  const { schedulesByUser } = useGlobalSearchData();
  const handleSelectItem = useCallback(
    (index: number) => {
      let currentIdx = 0;

      if (index < filteredActions.length) {
        const action = filteredActions[index];
        navigation.handleSettingAction(action.id);
        return;
      }
      currentIdx += filteredActions.length;

      if (index < currentIdx + resources.length) {
        const resource = resources[index - currentIdx];
        navigation.navigateToResource(resource.resource.id);
        return;
      }
      currentIdx += resources.length;

      if (index < currentIdx + people.length) {
        const person = people[index - currentIdx];
        navigation.navigateToPerson(person, schedulesByUser[person.id] || []);
        return;
      }
      currentIdx += people.length;

      if (index < currentIdx + filteredOffices.length) {
        const office = filteredOffices[index - currentIdx];
        navigation.navigateToOffice(office.id);
        return;
      }
      currentIdx += filteredOffices.length;

      if (index < currentIdx + filteredSettings.length) {
        const setting = filteredSettings[index - currentIdx];
        if (setting.type === "action") {
          navigation.handleSettingAction(setting.id);
        } else if (setting.url) {
          navigation.navigateToSetting(setting.url);
        }
      }
    },
    [
      filteredActions,
      resources,
      people,
      filteredOffices,
      filteredSettings,
      navigation,
      schedulesByUser
    ]
  );

  const handleSelectSetting = useCallback(
    (setting: Setting) => {
      if (setting.type === "action") {
        navigation.handleSettingAction(setting.id);
      } else if (setting.url) {
        navigation.navigateToSetting(setting.url);
      }
    },
    [navigation]
  );

  return {
    handleSelectItem,
    handleSelectSetting
  };
}
